import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Controller, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { useMutation } from '@apollo/client';
import { camelCase } from 'lodash';
import { faArrowUpRightFromSquare, faExclamationTriangle, faGear, faPlus } from '@fortawesome/pro-regular-svg-icons';
import Button from '../../../core/components/Button.tsx';
import SelectField from '../../../core/components/Fields/SelectField.tsx';
import TagsField from '../../../core/components/Fields/TagsField.tsx';
import { withIcon } from '../../../core/components/Icon.tsx';
import Popover from '../../../core/components/Popover.tsx';
import Switch from '../../../core/components/Switch.tsx';
import { useToaster } from '../../../core/components/Toast.tsx';
import Tooltip from '../../../core/components/Tooltip.tsx';
import { useApplicationContext } from '../../../core/contexts/ApplicationContext.tsx';
import classnames from '../../../core/utils/classnames.tsx';
import { url } from '../../../core/utils/link.ts';
import { canManageChannels } from '../../../users/utils.ts';
import RuleFieldsBox from '../../components/fineTuning/RuleFieldsBox.tsx';
import {
    getRuleDraft,
    getRuleTitle,
    NEW_RULE_ID,
    Rule,
    RULE_LEVELS,
    RuleFormValues,
    RuleLevel,
    toRuleFormValues,
    useRules,
    validateRuleClients,
} from '../../components/fineTuning/rules.ts';
import { mapDocumentTypeToGqlDocumentType } from '../../constants.ts';
import { useGetFieldLabel, useLlmExtractorInputs, useLlmExtractorOutputFieldNames } from '../../llmExtractorFields.ts';
import { REPROCESS_DOCUMENT, UPDATE_LLM_EXTRACTOR_CONFIG } from '../../queries.ts';
import { useAssistanceContext } from './AssistanceContext.tsx';
import AssistanceSection from './AssistanceSection.tsx';
import { GROUP_SEPARATOR } from './useDocumentConfig.ts';

const GearIcon = withIcon(faGear);
const PopOutIcon = withIcon(faArrowUpRightFromSquare);
const WarningIcon = withIcon(faExclamationTriangle);
const AddIcon = withIcon(faPlus);

interface FineTuningPopoverProps {
    // the header field group of the gear; none for the line items
    fieldName?: string;
    level: RuleLevel;
}

// the rules of the level that run for this document, to edit right here and reprocess the document with
const FineTuningPopover = ({ fieldName, level }: FineTuningPopoverProps) => {
    const { t } = useTranslation(['assistance', 'config']);
    const getFieldLabel = useGetFieldLabel();
    const { publishToast } = useToaster();
    const { user } = useApplicationContext();
    const { record, document, documentConfiguration, refetchData } = useAssistanceContext();
    const navigate = useNavigate();
    const [isOpen, setOpen] = useState(false);
    const [isSaving, setSaving] = useState(false);
    const [selectedRuleId, setSelectedRuleId] = useState(NEW_RULE_ID);
    const form = useForm<RuleFormValues>({ defaultValues: toRuleFormValues({}), mode: 'onTouched' });
    // a rule created by a save that failed afterwards is updated by the next save, not created a second time
    const createdRuleRef = useRef<Pick<Rule, 'id' | 'isDefault'> | null>(null);

    const channelId = record?.channel?.id;
    const gqlDocumentType = mapDocumentTypeToGqlDocumentType[documentConfiguration?.documentType];
    const clientPartitionId = document?.[camelCase(document?.clientPartitionFieldName)]?.customerNumber;
    // the rule endpoints need the channels permission
    const isAvailable = canManageChannels(user) && !!documentConfiguration?.supportsAgenticExtraction;

    const { rules, refetch } = useRules(level, channelId, gqlDocumentType, !isAvailable);
    // loaded before the popover opens, since the gear's colour depends on them
    const outputs = useLlmExtractorOutputFieldNames(channelId, gqlDocumentType, level, !isAvailable);
    const inputs = useLlmExtractorInputs(channelId, gqlDocumentType, level, !isOpen);

    const [createRule] = useMutation(RULE_LEVELS[level].createMutation);
    const [updateRule] = useMutation(UPDATE_LLM_EXTRACTOR_CONFIG);
    const [reprocessDocument] = useMutation(REPROCESS_DOCUMENT);

    // a field group stands for its first subfield
    const subfields = fieldName ? outputs.filter((name) => name.startsWith(fieldName + GROUP_SEPARATOR)) : [];
    const outputField = subfields[0] ?? fieldName;

    // the backend runs the default rule first, then the rule for the document's client
    const appliedRules = rules.filter(
        (rule) => rule.isDefault || (clientPartitionId && rule.partitionIds?.includes(clientPartitionId))
    );
    const writesField = (rule: Rule) => !outputField || !!rule.fields?.includes(outputField);
    const isInAppliedRule = appliedRules.some(writesField);
    const isAppliedRuleEnabled = appliedRules.some((rule) => rule.isEnabled && writesField(rule));
    const clientRule = clientPartitionId && rules.find((rule) => rule.partitionIds?.includes(clientPartitionId));
    const hasDefault = rules.some((rule) => rule.isDefault);

    const selectedRule = rules.find(({ id }) => id === selectedRuleId) ?? null;
    const otherRules = rules.filter(({ id }) => id !== selectedRuleId);
    // a new rule without client numbers becomes the default rule, as long as there is none
    const canBeDefault = !selectedRule && !hasDefault;

    const select = (ruleId: string) => {
        const rule = rules.find(({ id }) => id === ruleId);
        const isCreated = ruleId === createdRuleRef.current?.id;
        if (!isCreated) createdRuleRef.current = null;
        setSelectedRuleId(ruleId);
        const clientPartitionIds = clientPartitionId ? [clientPartitionId] : [];
        if (!rule) {
            const { values } = getRuleDraft(level, rules, undefined, t, outputField ? [outputField] : []);
            form.reset({ ...values, partitionIds: clientPartitionIds });
            return;
        }
        const savedValues = toRuleFormValues(rule);
        // the update that gives a created client rule the document's client failed with the save
        const values =
            isCreated && !rule.isDefault && !savedValues.partitionIds.length
                ? { ...savedValues, partitionIds: clientPartitionIds }
                : savedValues;
        form.reset(
            outputField && !values.outputFields.includes(outputField)
                ? { ...values, outputFields: [...values.outputFields, outputField].toSorted() }
                : values
        );
    };

    const handleOpenChange = (open: boolean) => {
        // a rule created by a failed save may have no client numbers yet, so it is opened again by its id
        if (open) {
            select(
                createdRuleRef.current?.id ?? (clientRule || rules.find((rule) => rule.isDefault))?.id ?? NEW_RULE_ID
            );
        }
        setOpen(open);
    };

    // the popover leaves the rule's other settings as they are
    const saveRule = async (formData: RuleFormValues) => {
        const savedRule = selectedRule ?? createdRuleRef.current;
        const isDefault = savedRule ? !!savedRule.isDefault : !formData.partitionIds.length;
        let ruleId = savedRule?.id;
        if (!ruleId) {
            const result = await createRule({ variables: { documentType: gqlDocumentType, channelId, isDefault } });
            ruleId = result.data?.created?.llmExtractorConfig?.id;
            createdRuleRef.current = { id: ruleId, isDefault };
            setSelectedRuleId(ruleId);
        }
        await updateRule({
            variables: {
                id: ruleId,
                fields: formData.outputFields,
                prompt: formData.prompt,
                documentInput: formData.inputFields,
                isEnabled: formData.isEnabled,
                ...(!isDefault && { partitionIds: formData.partitionIds }),
            },
        });
        await refetch();
        createdRuleRef.current = null;
        return ruleId;
    };

    const handleSaveError = (e: unknown) => {
        console.error(e);
        // a rule created before the save failed is on the backend, so it joins the list and stays selected
        if (createdRuleRef.current) refetch();
        const errorMessage = e instanceof Error ? e.message : t('assistance:fineTuning.saveError');
        publishToast({ description: errorMessage, status: 'error' });
    };

    const saveAndReprocess = async (formData: RuleFormValues) => {
        setSaving(true);
        try {
            await saveRule(formData);
            await reprocessDocument({ variables: { documentType: gqlDocumentType, recordId: record?.id } });
            await refetchData();
            publishToast({ description: t('assistance:fineTuning.saveSuccess'), status: 'success' });
            setOpen(false);
        } catch (e) {
            handleSaveError(e);
        } finally {
            setSaving(false);
        }
    };

    const saveAndOpenInConfig = async (formData: RuleFormValues) => {
        setSaving(true);
        try {
            const ruleId = await saveRule(formData);
            setOpen(false);
            navigate(
                url(documentConfiguration.CHANNEL_PATH + '/config/:tab?/:configId?', {
                    channelId,
                    tab: 'fine-tuning',
                    configId: ruleId,
                })
            );
        } catch (e) {
            handleSaveError(e);
        } finally {
            setSaving(false);
        }
    };

    if (!isAvailable) return null;

    const clientsHelp = canBeDefault
        ? t('assistance:fineTuning.clientsHelp')
        : hasDefault
          ? t('config:configPage.fineTuning.runsFor.selectedHelpDefaultExists')
          : t('config:configPage.fineTuning.runsFor.selectedHelp');
    const TriggerButton = level === 'header' ? AssistanceSection.FieldGroupIconButton : AssistanceSection.IconButton;

    return (
        <Popover open={isOpen} onOpenChange={handleOpenChange}>
            <Tooltip
                content={
                    isInAppliedRule
                        ? t('assistance:fineTuning.gearTooltipEdit')
                        : t('assistance:fineTuning.gearTooltipAdd')
                }
                side="top"
            >
                <Popover.Trigger asChild>
                    <TriggerButton
                        className={classnames({
                            'text-content-brand-strong': isInAppliedRule && isAppliedRuleEnabled,
                            'text-content-warning-strong': isInAppliedRule && !isAppliedRuleEnabled,
                        })}
                    >
                        <GearIcon />
                    </TriggerButton>
                </Popover.Trigger>
            </Tooltip>
            <Popover.Content className="w-[580px]" align="end" side="bottom">
                <form onSubmit={form.handleSubmit(saveAndReprocess)} className="flex flex-col gap-2.5">
                    <h3 className="text-sm font-semibold text-content-strong">{t(RULE_LEVELS[level].titleKey)}</h3>

                    <div className="flex gap-2 items-center">
                        <SelectField
                            options={rules.map((rule) => ({ label: getRuleTitle(rule, t), value: rule.id }))}
                            value={selectedRule?.id || ''}
                            onValueChange={(value) => select(value ?? NEW_RULE_ID)}
                            renderValue={(_, selectedOption) =>
                                selectedOption ? (
                                    <span className="text-content-strong">{selectedOption.label}</span>
                                ) : (
                                    <span className="text-content-weak">
                                        {t('assistance:fineTuning.newRulePlaceholder')}
                                    </span>
                                )
                            }
                            className="flex-1"
                            renderOption={(option) => <span className="text-content-strong">{option.label}</span>}
                        />
                        <Button
                            type="button"
                            variant="outline"
                            className="text-sm whitespace-nowrap"
                            onClick={() => select(NEW_RULE_ID)}
                            disabled={!selectedRule}
                        >
                            <AddIcon /> {t('config:configPage.fineTuning.addRule')}
                        </Button>
                    </div>
                    {hasDefault && !selectedRule?.isDefault && (
                        <p className="text-xs text-content-weak">{t('assistance:fineTuning.layeringHint')}</p>
                    )}

                    {subfields.length > 0 && fieldName && (
                        <div className="flex gap-2 items-center rounded-lg bg-surface-warning-medium p-2 text-xs text-content-strong border border-solid border-line-warning-default">
                            <WarningIcon className="flex-none text-content-warning-strong" />
                            <span>
                                {t('assistance:fineTuning.nestedFieldNotice', {
                                    parentField: getFieldLabel(fieldName),
                                    resolvedField: getFieldLabel(subfields[0]),
                                })}
                            </span>
                        </div>
                    )}

                    {!selectedRule?.isDefault && (
                        <Controller
                            name="partitionIds"
                            control={form.control}
                            rules={{
                                validate: (partitionIds) =>
                                    validateRuleClients(partitionIds, otherRules, t, canBeDefault),
                            }}
                            render={({ field, fieldState }) => (
                                <div className="flex flex-col gap-0.5">
                                    <label className="text-xs font-medium text-content-strong">
                                        {t('config:configPage.fineTuning.runsFor.clientNumbers')}
                                    </label>
                                    <p className="text-xs text-content-weak">{clientsHelp}</p>
                                    <TagsField
                                        value={field.value}
                                        onValueChange={field.onChange}
                                        onBlur={field.onBlur}
                                        placeholder={t('config:configPage.fineTuning.runsFor.clientsPlaceholder')}
                                    />
                                    {fieldState.error && (
                                        <span className="text-xs text-content-danger-weak">
                                            {fieldState.error.message}
                                        </span>
                                    )}
                                </div>
                            )}
                        />
                    )}

                    <div className="flex flex-col gap-0.5">
                        <span className="text-xs font-medium text-content-strong">
                            {t('config:configPage.fineTuning.instructions')}
                        </span>
                        <RuleFieldsBox
                            form={form}
                            inputs={inputs}
                            outputs={outputs}
                            gqlDocumentType={gqlDocumentType}
                        />
                    </div>

                    <Controller
                        name="isEnabled"
                        control={form.control}
                        render={({ field }) => (
                            <div className="flex items-center justify-between">
                                <label className="text-xs font-medium text-content-strong">
                                    {t('config:configPage.fineTuning.active')}
                                </label>
                                <Switch value={field.value} onValueChange={field.onChange} />
                            </div>
                        )}
                    />

                    <div className="flex items-center justify-between pt-2 border-t border-solid border-line-weak">
                        <Tooltip content={t('assistance:fineTuning.openInConfigTooltip')} side="top">
                            <Button
                                type="button"
                                variant="ghost"
                                onClick={form.handleSubmit(saveAndOpenInConfig)}
                                className="text-sm"
                                disabled={isSaving}
                            >
                                <PopOutIcon />
                            </Button>
                        </Tooltip>
                        <div className="flex gap-2">
                            <Button type="button" variant="outline" onClick={() => setOpen(false)} className="text-sm">
                                {t('assistance:fineTuning.discardChanges')}
                            </Button>
                            <Button type="submit" variant="primary" className="text-sm" disabled={isSaving}>
                                {t('assistance:fineTuning.saveAndReprocess')}
                            </Button>
                        </div>
                    </div>
                </form>
            </Popover.Content>
        </Popover>
    );
};

export default FineTuningPopover;
